import {
  ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { Divider } from "../Divider";
import { Checkbox } from "../inputs/CheckBox";
import { MixModal } from "./MixModal";
import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Text } from "components/core/Text";
import { Tooltip } from "components/core/Tooltip";
import { Button } from "components/core/buttons/Button";
import {
  Mix_Content_Tag_Enum_Enum,
  useSessionQuery,
  useUpdateMeMutation,
  useUpsertUserInterestTagsMutation,
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { fireEvent } from "lib/ga";
import { AppContext } from "contexts/AppContext";
import {
  EXTENSION_STORE_URLS,
  MIX_CONTENT_TAG_ENUM_KOREAN,
  ORDERED_CAREERS,
  ORDERED_MIX_CONTENT_TAG_ENUM,
  ORDERED_POSITION,
  POSITION_KOREAN,
  Position,
  SHINHAN_INVEST_TAG_LIST,
} from "utils/enum";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CardList = styled.div`
  --card-max-width: 150px;

  display: grid;
  grid-gap: 8px;
  grid-template-columns:
    minmax(0, var(--card-max-width)) minmax(0, var(--card-max-width))
    minmax(0, var(--card-max-width));
  width: 100%;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
`;

const Card = styled.button<{ selected: boolean }>`
  max-width: var(--card-max-width);
  width: 100%;
  height: 54px;
  border-radius: 12px;
  word-break: keep-all;

  @media (max-width: 768px) {
    max-width: 100%;
  }

  ${({ theme }) => theme.typography.subtitle2};

  ${({ selected, theme }) =>
    selected
      ? css`
          background-color: ${theme.palette.subcolor.blue};
          color: ${theme.palette.common.white.main};
          font-weight: 700;
        `
      : css`
          background-color: ${theme.palette.grey[100]};
          color: ${theme.palette.text.secondary};
          font-weight: 400;
        `};
`;

enum Stage {
  position,
  career,
  subscribeEmail,
  installExtension,
  selectInterestTag,
}

type SelectType<T = any> = (data: { [key in Stage]: T }) => T;

export const CareerModal = ({
  me,
  close,
  onRequestClose,
  ...props
}: ComponentProps<typeof MixModal> & {
  me: NonNullable<NonNullable<ReturnType<typeof useSessionQuery>["data"]>["session"]["me"]>;
  close: () => Promise<void>;
}) => {
  const theme = useTheme();
  const router = useRouter();
  const { refetch } = useAuth();
  const { browserName, deviceType } = useContext(AppContext);
  const [upsertUserInterestTags] = useUpsertUserInterestTagsMutation();

  const defaultTags = useMemo(
    () =>
      ORDERED_MIX_CONTENT_TAG_ENUM.filter(
        (tag) => !(SHINHAN_INVEST_TAG_LIST as string[]).includes(tag),
      ),
    [],
  );

  const [stage, setStage] = useState<Stage>(Stage.position);
  const [position, setPosition] = useState<Position | null>(null);
  const [career, setCareer] = useState<string | null>(null);
  const [subscribeEmail, setSubscribeEmail] = useState<boolean>(
    me?.subscribeEmail ?? false,
  );
  const [selectedTags, setSelectedTags] = useState(
    new Set<Mix_Content_Tag_Enum_Enum>(),
  );
  const [isFirstToggle, setIsFirstToggle] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  // Browser detection for extension install modal
  const getExtensionUrl = useCallback(() => {
    const browserKey = browserName.toUpperCase();
    return EXTENSION_STORE_URLS[browserKey]?.url || EXTENSION_STORE_URLS.CHROME?.url || "";
  }, [browserName]);

  const shouldShowExtensionModal = useMemo(() => {
    const isChromiumBased =
      typeof navigator !== "undefined" &&
      !!navigator.userAgentData &&
      navigator.userAgentData.brands.some((data) => data.brand === "Chromium");

    return (
      isChromiumBased &&
      deviceType === "desktop" &&
      EXTENSION_STORE_URLS[browserName.toUpperCase()]
    );
  }, [browserName, deviceType]);

  // 초기에 모든 태그를 선택된 상태로 설정
  useEffect(() => {
    const allTags = new Set([
      ...defaultTags.map((tag) => tag as Mix_Content_Tag_Enum_Enum),
      ...(me.group_id === "shinhaninvest"
        ? (SHINHAN_INVEST_TAG_LIST as Mix_Content_Tag_Enum_Enum[])
        : []),
    ]);
    setSelectedTags(allTags);
  }, [defaultTags, me.group_id]);

  const isNoTagsSelected = selectedTags.size === 0 && !isFirstToggle;

  // 태그가 선택되면 tooltip 숨기기
  useEffect(() => {
    if (!isNoTagsSelected) {
      setShowTooltip(false);
    }
  }, [isNoTagsSelected]);

  const toggleTag = async (tag: Mix_Content_Tag_Enum_Enum) => {
    if (isFirstToggle) {
      // 첫 토글 시: 현재 선택된 태그들(OFF한 태그 제외)만 upsert
      setIsFirstToggle(false);

      // 로컬 상태 먼저 업데이트 (OFF한 태그 제거)
      const updatedTags = new Set(selectedTags);
      updatedTags.delete(tag);
      setSelectedTags(updatedTags);

      try {
        // 업데이트된 태그들만 upsert
        if (updatedTags.size > 0) {
          const tagObjects = Array.from(updatedTags).map((tagValue, index) => ({
            user_id: me.id,
            tag_value: tagValue as Mix_Content_Tag_Enum_Enum,
            priority: index + 1,
          }));

          await upsertUserInterestTags({
            variables: {
              objects: tagObjects,
            },
          });
        }
      } catch (error) {
        setIsFirstToggle(true); // 실패 시 되돌리기
        // 실패 시 로컬 상태도 되돌리기
        setSelectedTags(selectedTags);
      }
    } else {
      // 일반 토글 로직
      setSelectedTags((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(tag)) {
          newSet.delete(tag);
        } else {
          newSet.add(tag);
        }
        return newSet;
      });
    }
  };

  const toggleAllTags = async (tagList: readonly string[]) => {
    const allSelected = tagList.every((tag) =>
      selectedTags.has(tag as Mix_Content_Tag_Enum_Enum),
    );

    if (isFirstToggle) {
      // 첫 토글 시: 전체 선택/해제 처리
      setIsFirstToggle(false);

      if (allSelected) {
        // 전체 해제: 해당 태그 그룹을 모두 제거
        const updatedTags = new Set(selectedTags);
        tagList.forEach((tag) => {
          updatedTags.delete(tag as Mix_Content_Tag_Enum_Enum);
        });
        setSelectedTags(updatedTags);

        try {
          // 업데이트된 태그들만 upsert
          if (updatedTags.size > 0) {
            const tagObjects = Array.from(updatedTags).map(
              (tagValue, index) => ({
                user_id: me.id,
                tag_value: tagValue as Mix_Content_Tag_Enum_Enum,
                priority: index + 1,
              }),
            );

            await upsertUserInterestTags({
              variables: {
                objects: tagObjects,
              },
            });
          }
        } catch (error) {
          setIsFirstToggle(true);
          setSelectedTags(selectedTags); // 실패 시 되돌리기
        }
      } else {
        // 전체 선택: 해당 태그 그룹을 모두 추가
        const updatedTags = new Set(selectedTags);
        tagList.forEach((tag) => {
          updatedTags.add(tag as Mix_Content_Tag_Enum_Enum);
        });
        setSelectedTags(updatedTags);

        try {
          // 업데이트된 태그들만 upsert
          const tagObjects = Array.from(updatedTags).map((tagValue, index) => ({
            user_id: me.id,
            tag_value: tagValue as Mix_Content_Tag_Enum_Enum,
            priority: index + 1,
          }));

          await upsertUserInterestTags({
            variables: {
              objects: tagObjects,
            },
          });
        } catch (error) {
          setIsFirstToggle(true);
          setSelectedTags(selectedTags); // 실패 시 되돌리기
        }
      }
    } else {
      // 일반 토글 로직
      if (allSelected) {
        // 전체 해제: 선택된 태그들을 한 번에 해제
        setSelectedTags((prev) => {
          const newSet = new Set(prev);
          tagList.forEach((tag) => {
            if (newSet.has(tag as Mix_Content_Tag_Enum_Enum)) {
              newSet.delete(tag as Mix_Content_Tag_Enum_Enum);
            }
          });
          return newSet;
        });
      } else {
        // 전체 선택: 선택되지 않은 태그들을 한 번에 선택
        setSelectedTags((prev) => {
          const newSet = new Set(prev);
          tagList.forEach((tag) => {
            if (!newSet.has(tag as Mix_Content_Tag_Enum_Enum)) {
              newSet.add(tag as Mix_Content_Tag_Enum_Enum);
            }
          });
          return newSet;
        });
      }
    }
  };

  const isAllTagsSelected = (tagList: readonly string[]) => {
    return (
      tagList.length > 0 &&
      tagList.every((tag) => selectedTags.has(tag as Mix_Content_Tag_Enum_Enum))
    );
  };

  const select = useCallback<SelectType>((data) => data[stage], [stage]);

  const [updateMe] = useUpdateMeMutation({
    onCompleted: async (response) => {
      if (response.update_mix_user_by_pk) {
        const { career, position } = response.update_mix_user_by_pk;
        const prev_webview_access = router.query.prev_webview_access === "true";

        // window.fbq("track", "CompleteRegistration", {
        //   method: me?.method,
        // });

        fireEvent({
          event: "sign_up",
          params: {
            method: me?.method,
            subscribeEmail,
            prev_webview_access,
          },
        });

        fireEvent({
          params: {
            career,
            position,
          },
        });
      }

      await close();
      router.reload(); // clear gtm static params
    },
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <MixModal {...props}>
      <div
        css={css`
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: 600px;
          padding: 56px 16px;
          background-color: ${theme.mode === "dark" ? theme.palette.common.black.main : theme.palette.common.white.main};
          border-radius: 24px;
          margin: 0 24px;

          & * {
            text-align: center;
          }

          @media (max-width: 768px) {
            padding: 48px 32px 32px;
            border-radius: 20px;
          }

          ${(stage === Stage.subscribeEmail) &&
          css`
            max-width: 375px;
            padding: 40px 16px;
          `}

          ${(stage === Stage.installExtension) &&
          css`
            max-width: 360px;
            padding: 33px 16px;
          `}
        `}
      >
        <Text
          css={css`
            white-space: pre-wrap;
            margin-bottom: 40px;

            @media (max-width: 768px) {
              ${theme.typography.h3};
              margin-bottom: 32px;
            }

            ${(stage === Stage.subscribeEmail || stage === Stage.installExtension) &&
            css`
              margin-bottom: 8px;
            `}
          `}
          variant="h2"
          tag="header"
        >
          {select({
            [Stage.position]: "나의 직무를 선택해주세요.",
            [Stage.career]: "나의 연차를 선택해주세요.",
            [Stage.selectInterestTag]: (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 4px;
                `}
              >
                <div>관심 주제를 선택해 주세요.</div>
                <Text
                  variant="body1"
                  color="text.secondary"
                  css={css`
                    @media (max-width: 768px) {
                      ${theme.typography.body3};
                    }
                  `}
                >
                  이후에도 언제든 변경할 수 있어요.
                </Text>
              </div>
            ),
            [Stage.subscribeEmail]: (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                `}
              >
                <div
                  css={css`
                    padding: 13px 0;
                    margin-bottom: 8px;
                  `}
                >
                  <div
                    css={css`
                      position: relative;
                      width: 74px;
                      height: 74px;
                    `}
                  >
                    <Image
                      alt="newsletter"
                      src="/img/newsletter.png"
                      fill={true}
                    />
                  </div>
                </div>
                {`믹스 뉴스레터를\n받아 보시겠어요?`}
              </div>
            ),
            [Stage.installExtension]: (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  margin: -0.1rem;
                `}
              >
                <div
                  css={css`
                    padding: 13px 0;
                    margin-bottom: 8px;
                    font-size: 73px;
                    width: 99px;
                    height: 89px;
                    margin-bottom: 0.8rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  🔥
                </div>
                <Text
                  variant="h4"
                  color="text.primary"
                >
                  확장 프로그램 사용자는 매주<br />
                  <span css={css`font-weight: 700; color: ${theme.palette.primary.main};`}>평균 13개의 콘텐츠</span>
                  를 더 읽고 있어요!
                </Text>
              </div>
            ),
          })}
        </Text>
        <Content>
          {select({
            [Stage.position]: (
              <CardList>
                {ORDERED_POSITION.map(($position) => (
                  <Card
                    key={$position}
                    selected={position === $position}
                    onClick={() => {
                      setPosition($position);
                      setStage(Stage.career);
                    }}
                  >
                    {POSITION_KOREAN[$position]}
                  </Card>
                ))}
              </CardList>
            ),
            [Stage.career]: (
              <CardList>
                {ORDERED_CAREERS.map(($career) => (
                  <Card
                    key={$career}
                    selected={career === $career}
                    onClick={() => {
                      setCareer($career);
                      setStage(Stage.selectInterestTag);
                    }}
                  >
                    {$career}
                  </Card>
                ))}
              </CardList>
            ),
            [Stage.selectInterestTag]: (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: start;
                `}
              >
                {me.group_id === "shinhaninvest" && (
                  <>
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 16px;
                      `}
                    >
                      <Image
                        src="/img/logo/shinhaninvest.png"
                        alt="shinhaninvest"
                        width={24}
                        height={24}
                      />
                      <Text variant="button.small" color="text.primary">
                        신한투자증권 전용 카테고리
                      </Text>
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-wrap: wrap;
                        align-items: center;
                        justify-content: start;
                        gap: 2px;
                        margin-bottom: 8px;
                        cursor: pointer;
                      `}
                      onClick={() => toggleAllTags(SHINHAN_INVEST_TAG_LIST)}
                    >
                      <Checkbox
                        size="medium"
                        checked={isAllTagsSelected(SHINHAN_INVEST_TAG_LIST)}
                      />
                      <span
                        css={css`
                          color: ${theme.palette.primary.main};
                        `}
                      >
                        모든 카테고리
                      </span>
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-wrap: wrap;
                        justify-content: start;
                        gap: 12px;
                        margin-bottom: 40px;
                        max-width: 483px;
                      `}
                    >
                      {(
                        SHINHAN_INVEST_TAG_LIST as Mix_Content_Tag_Enum_Enum[]
                      ).map((tag) => (
                        <Button
                          key={tag}
                          variant="filled"
                          color={selectedTags.has(tag) ? "primary" : "grey"}
                          size="medium"
                          onClick={() => toggleTag(tag)}
                          css={css`
                            height: 27px;
                            padding: 16.5px 14px 14px 14px;
                            border-radius: 12px;
                            font-family: "Pretendard";
                            font-size: 12px;
                            font-weight: 600;
                            line-height: 139.9999976158142%;
                            transition: all 0.2s ease;

                            &:hover {
                              border-color: ${theme.palette.primary.main};
                              ${!selectedTags.has(tag) &&
                            css`
                                background-color: ${theme.palette.primary
                                .hover};
                              `}
                            }

                            @media (max-width: 768px) {
                              padding: 16px 10px;
                            }
                          `}
                        >
                          {MIX_CONTENT_TAG_ENUM_KOREAN[tag]}
                        </Button>
                      ))}
                    </div>
                    <Divider
                      css={css`
                        margin-bottom: 40px;
                      `}
                    />
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 16px;
                      `}
                    >
                      <Image
                        src="/img/logo/mix.png"
                        alt="mix "
                        width={24}
                        height={24}
                      />
                      <Text variant="button.small" color="text.primary">
                        믹스 기본 카테고리
                      </Text>
                    </div>
                  </>
                )}
                <div
                  css={css`
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: start;
                    gap: 2px;
                    margin-bottom: 8px;
                    cursor: pointer;
                  `}
                  onClick={() => toggleAllTags(defaultTags)}
                >
                  <Checkbox
                    size="medium"
                    checked={isAllTagsSelected(defaultTags)}
                  />
                  <span
                    css={css`
                      color: ${theme.palette.primary.main};
                    `}
                  >
                    모든 카테고리
                  </span>
                </div>
                <div
                  css={css`
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: start;
                    gap: 12px;
                    margin-bottom: 40px;
                    max-width: 483px;
                  `}
                >
                  {defaultTags.map((tag) => (
                    <Button
                      key={tag}
                      variant="filled"
                      color={selectedTags.has(tag) ? "primary" : "grey"}
                      size="medium"
                      onClick={() => toggleTag(tag)}
                      css={css`
                        height: 27px;
                        padding: 16.5px 14px 14px 14px;
                        border-radius: 12px;
                        font-family: "Pretendard";
                        font-size: 12px;
                        font-weight: 600;
                        line-height: 139.9999976158142%;
                        transition: all 0.2s ease;

                        &:hover {
                          border-color: ${theme.palette.primary.main};
                          ${!selectedTags.has(tag) &&
                        css`
                            background-color: ${theme.palette.primary.hover};
                          `}
                        }

                        @media (max-width: 768px) {
                          padding: 16px 10px;
                        }
                      `}
                    >
                      {MIX_CONTENT_TAG_ENUM_KOREAN[tag]}
                    </Button>
                  ))}
                </div>
              </div>
            ),
            [Stage.subscribeEmail]: (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Text
                  css={css`
                    color: ${theme.palette.text.disabled};
                    white-space: pre-wrap;
                    margin-bottom: 40px;
                  `}
                  variant="body3"
                >
                  {`오리지널 콘텐츠와 인기 콘텐츠를\n메일로 보내드려요.`}
                </Text>
                <div
                  css={css`
                    display: flex;
                    gap: 8px;

                    button {
                      width: 140px;
                    }
                  `}
                >
                  <Button
                    size="xl"
                    variant="filled"
                    color="grey"
                    onClick={() => {
                      setSubscribeEmail(false);

                      if (shouldShowExtensionModal) {
                        setStage(Stage.installExtension);
                      } else {
                        updateMe({
                          variables: {
                            id: me.id,
                            _set: {
                              position,
                              career,
                              subscribeEmail: false,
                            },
                          },
                        });
                      }
                    }}
                  >
                    다음에 할게요
                  </Button>
                  <Button
                    size="xl"
                    onClick={() => {
                      setSubscribeEmail(true);

                      if (shouldShowExtensionModal) {
                        setStage(Stage.installExtension);
                      } else {
                        updateMe({
                          variables: {
                            id: me.id,
                            _set: {
                              position,
                              career,
                              subscribeEmail: true,
                            },
                          },
                        });
                      }
                    }}
                  >
                    받아볼래요
                  </Button>
                </div>
              </div>
            ),
            [Stage.installExtension]: (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Text
                  css={css`
                    color: ${theme.palette.text.disabled};
                    white-space: pre-wrap;
                    margin-bottom: 40px;
                  `}
                  variant="body3"
                >
                  지금 바로 크롬/웨일 스토어에서 만나보세요.
                </Text>
                <div
                  css={css`
                    display: flex;
                    gap: 8px;

                    button {
                      width: 140px;
                    }
                  `}
                >
                  <Button
                    size="xl"
                    variant="filled"
                    color="grey"
                    onClick={() => {
                      // Complete the signup process regardless of shouldShowExtensionModal
                      updateMe({
                        variables: {
                          id: me.id,
                          _set: {
                            position,
                            career,
                            subscribeEmail,
                          },
                        },
                      });
                    }}
                  >
                    다음에 할게요
                  </Button>
                  <Button
                    size="xl"
                    onClick={() => {
                      window.open(getExtensionUrl(), "_blank");
                      // Complete the signup process regardless of shouldShowExtensionModal
                      updateMe({
                        variables: {
                          id: me.id,
                          _set: {
                            position,
                            career,
                            subscribeEmail,
                          },
                        },
                      });
                    }}
                  >
                    구경해 볼게요
                  </Button>
                </div>
              </div>
            ),
          })}
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;
              height: 32px;
              margin-top: 24px;

              @media (max-width: 768px) {
                margin-top: 16px;
              }

              ${(stage === Stage.subscribeEmail || stage === Stage.installExtension) &&
              css`
                height: 100%;
                margin-top: 0;

                @media (max-width: 768px) {
                  margin-top: 0;
                }
              `}
            `}
          >
            <div>
              {select({
                [Stage.position]: <></>,
                [Stage.career]: (
                  <Button
                    variant="background"
                    color="grey"
                    size="medium"
                    startIcon="ChevronLeft"
                    onClick={() => setStage(Stage.position)}
                  >
                    이전
                  </Button>
                ),
                [Stage.selectInterestTag]: (
                  <Button
                    variant="background"
                    color="grey"
                    size="medium"
                    startIcon="ChevronLeft"
                    onClick={() => setStage(Stage.career)}
                  >
                    이전
                  </Button>
                ),
                [Stage.subscribeEmail]: <></>,
                [Stage.installExtension]: <></>,
              })}
            </div>
            <div
              css={css`
                display: flex;
                justify-content: flex-end;
                color: ${theme.palette.text.placeholder};
                ${theme.typography.body3};
              `}
            >
              {select({
                [Stage.position]: "1/3",
                [Stage.career]: "2/3",
                [Stage.selectInterestTag]: (
                  <Tooltip
                    placement="top"
                    trigger="mouseover"
                    open={showTooltip || undefined}
                  >
                    <Tooltip.Trigger asChild={false}>
                      <Button
                        variant="filled"
                        color="secondary"
                        size="small"
                        disabled={isNoTagsSelected}
                        onClick={() => {
                          const tagsToUpsert = Array.from(selectedTags);
                          if (tagsToUpsert.length > 0) {
                            upsertUserInterestTags({
                              variables: {
                                objects: tagsToUpsert.map((tag, index) => ({
                                  user_id: me.id,
                                  tag_value: tag,
                                  priority: index + 1,
                                })),
                              },
                            });
                          }
                          setStage(Stage.subscribeEmail);
                        }}
                        css={css`
                          padding: 2px 8px;
                          font-size: 12px;
                        `}
                      >
                        확인
                      </Button>
                    </Tooltip.Trigger>
                    {isNoTagsSelected && (
                      <Tooltip.Content
                        css={css`
                          max-width: 260px;
                          padding: 8px 10px;
                          border-radius: 6px;
                          font-size: 12px;
                          word-break: keep-all;
                          z-index: 1000;
                        `}
                      >
                        카테고리를 최소 1개 이상 선택해 주세요
                      </Tooltip.Content>
                    )}
                  </Tooltip>
                ),
                [Stage.subscribeEmail]: "",
                [Stage.installExtension]: "",
              })}
            </div>
          </div>
        </Content>
      </div>
    </MixModal>
  );
};
